import type { ReactNode } from 'react';
import { PillTabs } from './ui';
import { AnalyzeIcon, ProfileIcon, ResumeIcon, ShieldIcon } from './ui/icons';

export type ShellTab = 'analyze' | 'profile' | 'resume';

const TABS: Array<{ id: ShellTab; label: string; icon: ReactNode }> = [
  { id: 'analyze', label: 'Analyze', icon: <AnalyzeIcon /> },
  { id: 'profile', label: 'Profile', icon: <ProfileIcon /> },
  { id: 'resume', label: 'Resume', icon: <ResumeIcon /> },
];

interface AppShellProps {
  activeTab: ShellTab;
  onTabChange: (tab: ShellTab) => void;
  children: ReactNode;
}

/**
 * Product frame: a refined brand area, a translucent sticky navigation bar with
 * pill-shaped tabs, and a constrained content column. The nav never overflows
 * on small screens because the tabs shrink and the brand collapses to a mark.
 */
export function AppShell({ activeTab, onTabChange, children }: AppShellProps) {
  return (
    <div className="min-h-screen">
      <header className="sticky top-0 z-30 border-b border-line/70">
        <div className="glass-strong">
          <div className="mx-auto flex max-w-shell items-center justify-between gap-3 px-4 py-3 sm:px-6">
            <div className="flex min-w-0 items-center gap-3">
              <BrandMark />
              <div className="min-w-0">
                <p className="truncate text-base font-semibold tracking-tight text-ink">
                  NextRole
                </p>
                <p className="hidden truncate text-xs text-ink-faint sm:block">
                  Factual, tailored resumes
                </p>
              </div>
            </div>

            <nav aria-label="Primary" className="flex min-w-0 items-center">
              <PillTabs
                tabs={TABS}
                active={activeTab}
                onChange={(id) => onTabChange(id)}
                ariaLabel="NextRole sections"
                className="no-scrollbar max-w-full overflow-x-auto overflow-y-hidden"
              />
            </nav>
          </div>
        </div>
      </header>

      <main id="main" className="mx-auto flex max-w-shell flex-col gap-6 px-4 py-6 sm:px-6 sm:py-8">
        {children}
      </main>

      <footer className="mx-auto max-w-shell px-4 pb-10 pt-4 sm:px-6">
        <div className="flex flex-col gap-2 border-t border-line pt-4 text-xs text-ink-faint sm:flex-row sm:items-center sm:justify-between">
          <p className="flex items-center gap-1.5">
            <ShieldIcon className="h-3.5 w-3.5" />
            Everything runs in your browser. Nothing is uploaded or saved.
          </p>
          <p>
            Match scores are an explainable keyword heuristic, not an ATS verdict.
          </p>
        </div>
      </footer>
    </div>
  );
}

function BrandMark() {
  return (
    <span
      aria-hidden="true"
      className="flex h-9 w-9 shrink-0 items-center justify-center rounded-pill bg-accent text-white shadow-card"
    >
      <svg viewBox="0 0 24 24" fill="none" className="h-5 w-5">
        <path
          d="M7 17V7l10 10V7"
          stroke="currentColor"
          strokeWidth="2.1"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    </span>
  );
}
